import React from "react";

import { redirect, RouteObject } from "react-router-dom";

import { App } from "@/ui/app/App";
import { NoMatchError, RouteCrashError, UnauthorizedError } from "@/ui/errors";
import { Logout } from "@/ui/shared/Logout";

import { serialiseQueryParams } from "./routing";

const ClassRoute = React.lazy(
  () => import("@/features/classlist/routes/class-route")
);
const TaskRoute = React.lazy(() => import("@/features/task/task-route"));
const HomeRoute = React.lazy(() => import("@/features/home/routes/home-route"));
const GroupsRoute = React.lazy(
  () => import("@/features/groups/routes/groups-route")
);
const InsightsRoute = React.lazy(() => import("@/ui/insights/InsightsRoute"));
const LearningAssuranceRoute = React.lazy(
  () => import("@/ui/learning-assurance/learning-assurance-route")
);
const OriginalityRoute = React.lazy(
  () => import("@/features/originality/routes/originality-route")
);
const GraderRoute = React.lazy(
  () => import("@/features/marking/routes/grader-route")
);
const QuestionLibraryRoute = React.lazy(
  () => import("@/features/question-library/routes/question-library-route")
);
const RubricRoute = React.lazy(
  () => import("@/features/rubric/routes/rubric-route")
);
const CharterFormRoute = React.lazy(
  () => import("@/features/charter/form/routes/charter-form-route")
);
const CharterFeedbackRoute = React.lazy(
  () => import("@/features/charter/feedback/routes/charter-feedback-route")
);
const AssessmentSettingsRoute = React.lazy(
  () => import("@/features/settings/routes/settings-route")
);
const SentimentRoute = React.lazy(
  () => import("@/features/sentiment/routes/sentiment-route")
);
const SocraticRoute = React.lazy(
  () => import("@/features/socratic/routes/socratic-route")
);
const WorkPreviewRoute = React.lazy(
  () => import("@/features/socratic/routes/work-preview-route")
);

/**
 * Some typing for the custom route handler.
 * Used to pass in additional information about the route.
 *
 * See <Route handle> in the react-router-dom docs about this.
 * There's currently no guidance on typing this.
 */
export interface CustomRouteHandler {
  /**
   * Whether this route is an Athena root. The current possible root routes are:
   * - Task
   * - Class
   * - Learning Assurance
   * - Insights
   * - Cadmus Marker (Grader)
   * - Question Library
   * - Rubric
   */
  root?:
    | "task"
    | "class"
    | "learning-assurance"
    | "originality"
    | "insights"
    | "grader"
    | "library"
    | "charter-form"
    | "charter-feedback"
    | "settings"
    | "rubric"
    | "home"
    | "groups"
    | "assign-dates"
    | "mark-peer"
    | "sentiment"
    | "work-preview";
}

export default [
  {
    path: "/logout",
    Component: Logout,
    errorElement: <Logout />,
  },
  {
    path: "/goodbye",
    Component: UnauthorizedError,
    errorElement: <RouteCrashError />,
  },
  {
    path: "/:tenant/assessment/:assessmentId/*",
    Component: App,
    errorElement: <RouteCrashError />,
    children: [
      {
        // redirect setup/* to task/*
        path: "setup/*",
        loader: ({ params, request }) => {
          const { tenant, assessmentId } = params;
          const parsedOriginalUrl = new URL(request.url);
          const originalSearchParamsString = serialiseQueryParams(
            parsedOriginalUrl.searchParams
          );
          const urlStart = `/${tenant}/assessment/${assessmentId}/task`;
          // attach any subpaths to the end of the redirect if there are any
          const redirectUrlPath =
            params["*"] != null && params["*"] !== ""
              ? `${urlStart}/${params["*"]}`
              : urlStart;
          // pass along any query params to the redirected route
          const redirectUrl = `${redirectUrlPath}?${originalSearchParamsString}`;
          return redirect(redirectUrl);
        },
      },
      {
        path: "task",
        index: true,
        loader: ({ params }) => {
          const { tenant, assessmentId } = params;
          return redirect(`/${tenant}/assessment/${assessmentId}/home`);
        },
      },
      {
        path: "task/edit",
        loader: ({ params }) => {
          const { tenant, assessmentId } = params;
          return redirect(`/${tenant}/assessment/${assessmentId}/home`);
        },
      },
      {
        path: "task/edit/*",
        loader: ({ params }) => {
          const { tenant, assessmentId } = params;
          return redirect(`/${tenant}/assessment/${assessmentId}/home`);
        },
      },
      {
        path: "task/:taskId/socratic/*",
        Component: SocraticRoute,
        loader: ({ params }) => {
          const { tenant, assessmentId, taskId } = params;
          const isValidTaskId = /^[a-f0-9-]{36}$/i.test(taskId ?? "");
          if (!isValidTaskId) {
            return redirect(`/${tenant}/assessment/${assessmentId}/home`);
          }
          return null;
        },
      },
      {
        path: "task/:taskId/*",
        Component: TaskRoute,
        loader: ({ params }) => {
          const { tenant, assessmentId, taskId } = params;
          const isValidTaskId = /^[a-f0-9-]{36}$/i.test(taskId ?? "");
          if (!isValidTaskId) {
            return redirect(`/${tenant}/assessment/${assessmentId}/home`);
          }
          return null;
        },
      },
      {
        path: "home/*",
        Component: HomeRoute,
        handle: {
          root: "home",
        } satisfies CustomRouteHandler,
      },
      {
        path: "groups/*",
        Component: GroupsRoute,
        handle: {
          root: "groups",
        } satisfies CustomRouteHandler,
      },
      // COMPAT - redirect to new groups route from old marking/groups route
      {
        path: "class/marking/groups",
        loader: ({ params }) => {
          const { tenant, assessmentId } = params;
          return redirect(`/${tenant}/assessment/${assessmentId}/groups`);
        },
      },
      {
        path: "class/*",
        Component: ClassRoute,
        handle: {
          root: "class",
        } satisfies CustomRouteHandler,
      },
      {
        path: "learning-assurance/*",
        Component: LearningAssuranceRoute,
        handle: {
          root: "learning-assurance",
        } satisfies CustomRouteHandler,
      },
      {
        path: "originality/*",
        Component: OriginalityRoute,
        handle: {
          root: "originality",
        } satisfies CustomRouteHandler,
      },
      {
        path: "insights/*",
        Component: InsightsRoute,
        handle: {
          root: "insights",
        } satisfies CustomRouteHandler,
      },
      {
        path: "grader/*",
        Component: GraderRoute,
        handle: {
          root: "grader",
        } satisfies CustomRouteHandler,
      },
      {
        path: "library/*",
        Component: QuestionLibraryRoute,
        handle: {
          root: "library",
        } satisfies CustomRouteHandler,
      },
      {
        path: "rubric/*",
        Component: RubricRoute,
        handle: {
          root: "rubric",
        } satisfies CustomRouteHandler,
      },
      {
        path: "group-charter-builder/*",
        Component: CharterFormRoute,
        handle: {
          root: "charter-form",
        } satisfies CustomRouteHandler,
      },
      {
        path: "charter/:charterId/*",
        Component: CharterFeedbackRoute,
        handle: {
          root: "charter-feedback",
        } satisfies CustomRouteHandler,
      },
      {
        path: "works/student/:studentId",
        Component: WorkPreviewRoute,
        handle: {
          root: "work-preview",
        } satisfies CustomRouteHandler,
      },
      {
        path: "sentiment/:groupId",
        Component: SentimentRoute,
        handle: {
          root: "sentiment",
        } satisfies CustomRouteHandler,
      },
      {
        path: "settings/*",
        Component: AssessmentSettingsRoute,
        handle: {
          root: "settings",
        } satisfies CustomRouteHandler,
      },
      {
        path: "*", // Catch all
        element: <NoMatchError />,
      },
    ],
  },
  {
    path: "*", // Catch all
    element: <NoMatchError />,
  },
] satisfies RouteObject[];
